import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  CheckCircle2, 
  Clock, 
  BookOpen,
  MessageSquare,
  RefreshCw,
  Download,
  AlertCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagerEnablementTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("executiveToken") || localStorage.getItem("chroToken") || localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/executive-enhanced/manager-enablement`, getAuthHeaders());
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching manager enablement data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A265]" />
      </div>
    );
  }

  const { summary, byRegion } = data;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1a3a3a]">Manager Readiness - All Regions</h2>
          <p className="text-sm text-muted-foreground">Tracking manager preparation for development conversations</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-[#1a3a3a]" />
              <span className="text-sm font-medium text-muted-foreground">Total Managers</span>
            </div>
            <p className="text-3xl font-bold text-[#1a3a3a]">{summary.totalManagers}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-muted-foreground">Managers Briefed</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{summary.briefed}/{summary.totalManagers}</p>
            <div className="flex items-center gap-2 mt-2">
              <Progress value={summary.briefedPercentage} className="flex-1 h-2" />
              <span className="text-sm font-semibold">{summary.briefedPercentage}%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-muted-foreground">Materials Downloaded</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{summary.materials}</p>
            <div className="flex items-center gap-2 mt-2">
              <Progress value={summary.materialsPercentage} className="flex-1 h-2" />
              <span className="text-sm font-semibold">{summary.materialsPercentage}%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-muted-foreground">Coaching Complete</span>
            </div>
            <p className="text-3xl font-bold text-purple-600">{summary.coaching}/{summary.totalManagers}</p>
            <div className="flex items-center gap-2 mt-2">
              <Progress value={summary.coachingPercentage} className="flex-1 h-2" />
              <span className="text-sm font-semibold">{summary.coachingPercentage}%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Regional Breakdown */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Enablement by Region</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {byRegion.map((region, i) => {
              const isFullyEnabled = region.briefedPercentage === 100;
              const needsAttention = region.coachingPercentage < 50;
              
              return (
                <Card key={i} className={`border ${needsAttention ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{region.flag}</span>
                        <span className="font-semibold text-sm">{region.regionName}</span>
                      </div>
                      {isFullyEnabled && (
                        <Badge className="bg-emerald-100 text-emerald-700">✓ Ready</Badge>
                      )}
                      {needsAttention && (
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Briefed</span>
                          <span className="font-semibold">{region.briefed}/{region.totalManagers}</span>
                        </div>
                        <Progress value={region.briefedPercentage} className="h-2" />
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Materials</span>
                          <span className="font-semibold">{region.materials}/{region.totalManagers}</span>
                        </div>
                        <Progress value={region.materialsPercentage} className="h-2" />
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Coaching</span>
                          <span className="font-semibold">{region.coaching}/{region.totalManagers}</span>
                        </div>
                        <Progress value={region.coachingPercentage} className="h-2" />
                      </div>
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      {region.totalManagers} managers
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Action Items */}
      <Card className="border-0 shadow-sm bg-amber-50 border-amber-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
            <AlertCircle className="w-5 h-5" />
            Action Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {byRegion.filter(r => r.briefedPercentage < 100).map((region, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <div>
                  <p className="font-medium text-sm">{region.flag} {region.regionName}</p>
                  <p className="text-xs text-muted-foreground">{region.totalManagers - region.briefed} managers not yet briefed</p>
                </div>
                <Button size="sm" variant="outline">Schedule Briefing</Button>
              </div>
            ))}
            {byRegion.filter(r => r.coachingPercentage < 50).map((region, i) => (
              <div key={`coaching-${i}`} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <div>
                  <p className="font-medium text-sm">{region.flag} {region.regionName}</p>
                  <p className="text-xs text-muted-foreground">{region.totalManagers - region.coaching} need coaching training</p>
                </div>
                <Button size="sm" variant="outline">Assign Coaching</Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
